import { useState, useCallback } from "react";

import { utils } from "@atlaskit/util-service-support";

import { NotificationGroup } from "../../types";

interface FetchNotificationsOptions {
    notificationLogUrl: string;
    continuationToken?: string;
    groupId?: string;
}

export const useWacNotifications = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchNotifications = useCallback(
        async ({
            notificationLogUrl,
            continuationToken,
            groupId
        }: FetchNotificationsOptions) => {
            setIsLoading(true);
            setError(null);

            const queryParams: Record<string, string | number> = {
                category: "direct",
                expand: "content.body",
                limit: 8
            };

            if (continuationToken) {
                queryParams["continuationToken"] = continuationToken;
            }

            // Determine the endpoint based on whether we're fetching more notifications for a specific group
            let url = `${notificationLogUrl}/api/3/notification-groups`;
            if (groupId && continuationToken) {
                // When expanding a group, use the group-specific endpoint
                url = `${notificationLogUrl}/api/3/notification-groups/${groupId}/notifications`;
            }

            try {
                const response = await utils.requestService<
                    | {
                          groups: NotificationGroup[];
                          continuationToken?: string;
                      }
                    | {
                          notifications: NotificationGroup["notifications"][0][];
                          continuationToken?: string;
                      }
                >(
                    {
                        url
                    },
                    {
                        queryParams,
                        requestInit: {
                            credentials: "include",
                            headers: {
                                "content-type": "application/json",
                                "Accept-Language": "en",
                                "x-app-name": "wac-notifications",
                                "x-app-version": "local"
                            }
                        }
                    }
                );

                setIsLoading(false);

                // Handle both response formats
                if ("groups" in response) {
                    // Store continuation token on the response object for later retrieval
                    const result = response.groups || [];
                    (result as unknown as Record<string, unknown>)[
                        "__continuationToken"
                    ] = response.continuationToken;
                    return result;
                } else if ("notifications" in response) {
                    // When expanding a group, wrap notifications in a group structure
                    return [
                        {
                            id: groupId || "",
                            notifications: response.notifications,
                            notificationContinuationToken:
                                response.continuationToken,
                            size: response.notifications.length,
                            additionalActors: []
                        }
                    ];
                }

                return [];
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : "Failed to fetch notifications"
                );
                setIsLoading(false);
                return [];
            }
        },
        []
    );

    return {
        isLoading,
        error,
        fetchNotifications
    };
};
